import apiFetch from "@/app/auth/apiFetch";
import type { NavigateFunction } from "react-router-dom";

const API_URL = import.meta.env.VITE_API_URL;

export type LinkedInPremiumType = "recruiter" | "sales" | "premium";

export interface AdminUserVerification {
  company_name: string;
  verification_type: "work_email" | "recruiter_license" | string;
  verified_month: number;
  verified_year: number;
}

export interface AdminUserLinkedIn {
  profile_verified?: boolean | null;
  profile_oldest_year?: number | null;
  profile_health_synced_at?: string | null;
  account_urn?: string | null;
  premium?: boolean | null;
  premium_type?: LinkedInPremiumType | null;
  premium_synced_at?: string | null;
  // "À propos de ce membre" du recruteur (plugin v1.5+).
  member_since_month?: number | null;
  member_since_year?: number | null;
  contact_info_fresh?: string | null;
  photo_fresh?: string | null;
  verifications?: AdminUserVerification[];
}

export interface AdminUserPlugin {
  is_connected?: boolean | null;
  last_seen?: string | null;
  version?: string | null;
}

// Contexte organisation B2B d'un compte (cf. GET /api/admin/users).
// null pour un compte B2C solo ou quand B2B_ENABLED=false côté backoffice.
export interface AdminUserOrganization {
  id: number;
  name: string;
  tier: string | null;
  is_business: boolean | null;
  member_role: string | null;
  purchased_seats?: number | null;
  active_seats?: number | null;
}

export interface AdminUserItem {
  user_id: string;
  registration_date: string;
  subscription_type: string;
  stripe_customer_id?: string | null;
  profile?: {
    email?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    enabled?: boolean | null;
  };
  organization?: AdminUserOrganization | null;
  linkedin?: AdminUserLinkedIn;
  // Tier consolide LinkedIn (cf. backoffice/src/features/admin/quotas.py).
  // Expose dans GET /api/admin/users pour afficher le badge "Ancien" dans
  // UsersPage sans payer N x /quotas.
  account_tier?: "new" | "trusted";
  account_tier_reason?:
    | "verified"
    | "premium"
    | "attached_90d"
    | "linkedin_profile_age"
    | "new";
  plugin?: AdminUserPlugin;
  // Kill-switch scraping par compte (cf. backoffice admin/scraping_router.py).
  scraping_disabled?: boolean | null;
  scraping_disabled_at?: string | null;
  scraping_disabled_by?: string | null;
}

export interface AdminUsersResponse {
  items: AdminUserItem[];
  count: number;
}

export interface RevenueProjectionResponse {
  // `mrr` = MRR B2C uniquement (rétro-compat). Le B2B est dans `b2b`.
  mrr: number;
  breakdown: Record<string, number>;
  counts: Record<string, number>;
  b2b?: {
    mrr_cents: number;
    mrr_eur: number;
    per_seat_orgs: number;
    currency: string;
  };
}

export async function listUsers(navigate): Promise<AdminUsersResponse> {
  const res = await apiFetch(`${API_URL}/admin/users`, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors du chargement des utilisateurs");
  return res.json();
}

export async function disableUser(userId: string, navigate): Promise<void> {
  const res = await apiFetch(`${API_URL}/admin/users/${userId}/disable`, { method: "POST" }, navigate);
  if (!res?.ok) throw new Error("Échec de la désactivation de l'utilisateur");
}

export async function enableUser(userId: string, navigate): Promise<void> {
  const res = await apiFetch(`${API_URL}/admin/users/${userId}/enable`, { method: "POST" }, navigate);
  if (!res?.ok) throw new Error("Échec de l'activation de l'utilisateur");
}

// Résumé renvoyé par DELETE /admin/users/{id} (cf. backoffice erasure_services.erase_user).
export interface ErasureSummary {
  user_id: string;
  branch: "none" | "b2c" | "b2b";
  organization_id: number | null;
  subscription_deleted: boolean;
  user_row_deleted: boolean;
  stripe_subscriptions_cancelled: string[];
  org_seat_freed: boolean;
  keycloak_deleted: boolean;
  redis_quota_keys_flushed: number;
  redis_pause_keys_cleared: number;
  es_cleanup: string;
  n8n_cleanup: string;
}

export async function deleteUser(userId: string, navigate): Promise<ErasureSummary | null> {
  const res = await apiFetch(`${API_URL}/admin/users/${userId}`, { method: "DELETE" }, navigate);
  if (!res?.ok && res?.status !== 204) {
    // Remonte le `detail` réel du backend (409 dernier admin, 502 Stripe/Keycloak, …)
    // au lieu d'un message générique qui masquait la cause.
    const detail = await res?.json().catch(() => null);
    throw new Error(detail?.detail ?? "Échec de la suppression de l'utilisateur");
  }
  if (res.status === 204) return null;
  const body = await res.json().catch(() => null);
  return (body?.erasure ?? null) as ErasureSummary | null;
}

// --------------- Kill-switch scraping (par compte / par worker) ---------------
// Coupe enrichissements candidats/écoles/entreprises, recherches et offres
// d'emploi. Les invitations et messages de campagne continuent.

export async function setUserScraping(userId: string, disabled: boolean, navigate): Promise<void> {
  const res = await apiFetch(
    `${API_URL}/admin/users/${userId}/scraping`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ disabled }),
    },
    navigate
  );
  if (!res?.ok) {
    const detail = await res?.json().catch(() => null);
    throw new Error(detail?.detail ?? "Échec de la mise à jour du scraping du compte");
  }
}

export async function setWorkerScraping(workerId: string, disabled: boolean, navigate): Promise<void> {
  const res = await apiFetch(
    `${API_URL}/admin/workers/${encodeURIComponent(workerId)}/scraping`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ disabled }),
    },
    navigate
  );
  if (!res?.ok) {
    const detail = await res?.json().catch(() => null);
    throw new Error(detail?.detail ?? "Échec de la mise à jour du scraping du plugin");
  }
}

// --------------- Automation pause (kill-switch v1 : sourcing / tout) ---------------
// mode 'scraping' : famille sourcing coupée, campagnes + checks continuent.
// mode 'all' : tout est coupé (le plugin se met en pause).
// Couvre les plugins authentifiés (colonne DB) ET anonymes (miroir Redis).

export type AutomationPauseMode = "scraping" | "all";

export interface AutomationPauseState {
  user_id: string;
  subscription_id: number;
  mode: AutomationPauseMode | null;
  until: string | null;
  reason: string | null;
  by: string | null;
  at: string | null;
  scraping_disabled_legacy: boolean;
  account_keys_mirrored: string[];
}

export interface AutomationPauseRequest {
  mode: AutomationPauseMode | "none";
  reason?: string | null;
  until?: string | null;
}

export async function getAutomationPause(
  userId: string,
  navigate
): Promise<AutomationPauseState> {
  const res = await apiFetch(
    `${API_URL}/admin/users/${userId}/automation-pause`,
    { method: "GET" },
    navigate
  );
  if (!res?.ok) throw new Error("Échec du chargement de l'état de pause du plugin");
  return res.json();
}

export async function setAutomationPause(
  userId: string,
  body: AutomationPauseRequest,
  navigate
): Promise<AutomationPauseState> {
  const res = await apiFetch(
    `${API_URL}/admin/users/${userId}/automation-pause`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
    navigate
  );
  if (!res?.ok) {
    const detail = await res?.json().catch(() => null);
    throw new Error(detail?.detail ?? "Échec de la mise à jour de la pause du plugin");
  }
  return res.json();
}

export interface GlobalPauseState {
  mode: AutomationPauseMode | null;
  reason?: string | null;
  by?: string | null;
  at?: string | null;
}

export async function getGlobalPause(navigate): Promise<GlobalPauseState> {
  const res = await apiFetch(
    `${API_URL}/admin/automation-pause/global`,
    { method: "GET" },
    navigate
  );
  if (!res?.ok) throw new Error("Échec du chargement du switch global");
  return res.json();
}

export async function setGlobalPause(
  body: { mode: AutomationPauseMode | "none"; reason?: string | null },
  navigate
): Promise<GlobalPauseState> {
  const res = await apiFetch(
    `${API_URL}/admin/automation-pause/global`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
    navigate
  );
  if (!res?.ok) {
    const detail = await res?.json().catch(() => null);
    throw new Error(detail?.detail ?? "Échec de la mise à jour du switch global");
  }
  return res.json();
}

export async function getRevenueProjection(navigate): Promise<RevenueProjectionResponse> {
  const res = await apiFetch(`${API_URL}/admin/revenue/projection`, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors du calcul de la projection");
  return res.json();
}

// --------------- User Quotas (tier-aware, cf. back/core/quotas.py) ---------------

export type AccountTier = "new" | "trusted";
export type AccountTierReason =
  | "verified"
  | "premium"
  | "attached_90d"
  | "linkedin_profile_age"
  | "new";
export type QuotaBucket = "invitation" | "message" | "scrape" | "search";

export interface QuotaWindow {
  used: number;
  cap: number;
}

export interface BucketQuotas {
  hourly: QuotaWindow;
  daily: QuotaWindow;
}

export interface UserQuotasResponse {
  user_id: string;
  account_tier: AccountTier;
  account_tier_reason: AccountTierReason;
  quotas: Record<QuotaBucket, BucketQuotas>;
}

export async function getUserQuotas(userId: string, navigate): Promise<UserQuotasResponse> {
  const res = await apiFetch(
    `${API_URL}/admin/users/${userId}/quotas`,
    { method: "GET" },
    navigate
  );
  if (!res?.ok) throw new Error("Erreur lors du chargement des quotas");
  return res.json();
}

// --------------- Elasticsearch & Admin Operations ---------------

export interface EsIndexInfo {
  exists: boolean;
  doc_count: number;
  size_bytes: number;
  error?: string;
}

export interface EsStatusResponse {
  enabled: boolean;
  indices: Record<string, EsIndexInfo>;
}

// Clés dynamiques : le backend renvoie un dict aligné sur les entités
// (candidates, companies, schools, skills, certifications, languages, interests,
// campaigns, subscriptions, messages). `Record` plutôt qu'interface figée — évite
// la dérive type↔backend (l'ancienne déclaration listait `meets`, jamais renvoyé,
// et omettait `skills`, bien renvoyé).
export type DbStatsResponse = Record<string, number>;

export interface ReindexJob {
  job_id: string;
  index_name: string;
  status: "pending" | "running" | "completed" | "failed" | "cancelled";
  progress: number;
  total: number;
  errors: number;
  error_detail?: string;
  started_at: string;
  finished_at?: string;
  temp_index?: string;
  cancel_requested?: string;
}

export async function getEsStatus(navigate: NavigateFunction): Promise<EsStatusResponse> {
  const res = await apiFetch(`${API_URL}/admin/elasticsearch/status`, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors de la recuperation du statut ES");
  return res.json();
}

export async function startReindex(navigate: NavigateFunction, indexName: string): Promise<{ job_id: string; status: string }> {
  const res = await apiFetch(`${API_URL}/admin/elasticsearch/reindex/${indexName}`, { method: "POST" }, navigate);
  if (res?.status === 409) throw new Error("Un reindex est deja en cours pour cet index");
  if (!res?.ok) throw new Error(`Erreur lors du lancement de la reindexation de ${indexName}`);
  return res.json();
}

export async function startReindexAll(navigate: NavigateFunction): Promise<{ jobs: Record<string, string>; skipped: string[] }> {
  const res = await apiFetch(`${API_URL}/admin/elasticsearch/reindex-all`, { method: "POST" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors du lancement de la reindexation complete");
  return res.json();
}

export async function getReindexJobs(navigate: NavigateFunction): Promise<ReindexJob[]> {
  const res = await apiFetch(`${API_URL}/admin/elasticsearch/reindex/jobs`, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors de la recuperation des jobs");
  return res.json();
}

export async function getReindexJob(navigate: NavigateFunction, jobId: string): Promise<ReindexJob> {
  const res = await apiFetch(`${API_URL}/admin/elasticsearch/reindex/jobs/${jobId}`, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors de la recuperation du job");
  return res.json();
}

export async function cancelReindexJob(navigate: NavigateFunction, jobId: string): Promise<void> {
  const res = await apiFetch(`${API_URL}/admin/elasticsearch/reindex/jobs/${jobId}/cancel`, { method: "POST" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors de l'annulation");
}

// --- Career metrics sweep (A-player / dominant company type), run by back/ ---

export type CareerSweepCoverage = {
  processed_candidates: number;
  with_career_metrics: number;
  with_progression_score: number;
  with_dominant_company_type: number;
  companies: number;
  classified_companies: number;
};

export type CareerSweepStatus = {
  version: string | null;
  phase: "companies" | "candidates" | "reindex" | "done" | null;
  company_cursor: number;
  candidate_cursor: number;
  enrich_cursor: number;
  company_enrichment_done: boolean;
  /** Reindex closing the sweep, while `phase` is "reindex". */
  reindex_job_id?: string | null;
  companies_max_id: number;
  processed_candidates_max_id: number;
  companies_progress: number;
  candidates_progress: number;
  coverage: CareerSweepCoverage;
};

export async function getCareerSweepStatus(navigate: NavigateFunction): Promise<CareerSweepStatus> {
  const res = await apiFetch(`${API_URL}/admin/career-sweep`, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors de la recuperation de l'etat du recalcul des parcours");
  return res.json();
}

export async function restartCareerSweep(navigate: NavigateFunction): Promise<{ status: string }> {
  const res = await apiFetch(`${API_URL}/admin/career-sweep/restart`, { method: "POST" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors de la relance du recalcul des parcours");
  return res.json();
}

export async function deleteEsIndex(navigate: NavigateFunction, indexName: string): Promise<{ status: string; index: string }> {
  const res = await apiFetch(`${API_URL}/admin/elasticsearch/index/${indexName}`, { method: "DELETE" }, navigate);
  if (!res?.ok) throw new Error(`Erreur lors de la suppression de l'index ${indexName}`);
  return res.json();
}

export async function clearCache(navigate: NavigateFunction): Promise<{ status: string; cleared: number }> {
  const res = await apiFetch(`${API_URL}/admin/cache/clear`, { method: "POST" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors du vidage du cache");
  return res.json();
}

export interface EsIndexMappingResponse {
  index: string;
  mappings: Record<string, unknown>;
  settings: Record<string, unknown>;
}

export async function getIndexMapping(navigate: NavigateFunction, indexName: string): Promise<EsIndexMappingResponse> {
  const res = await apiFetch(`${API_URL}/admin/elasticsearch/index/${indexName}/mapping`, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error(`Erreur lors de la recuperation du mapping de ${indexName}`);
  return res.json();
}

export async function getDbStats(navigate: NavigateFunction): Promise<DbStatsResponse> {
  const res = await apiFetch(`${API_URL}/admin/db/stats`, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors de la recuperation des stats DB");
  return res.json();
}

export interface EnrichStaleResponse {
  entity: string;
  before: string;
  queued: number;
}

export function getExportUrl(): string {
  return `${API_URL}/admin/data-transfer/export`;
}

export interface ImportJobResponse {
  job_id: string;
  status: string;
  processed: number;
  total: number;
  started_at: string;
  finished_at?: string;
  error?: string;
  skills_added: number;
  skills_existing: number;
  industries_added: number;
  industries_existing: number;
  companies_added: number;
  companies_updated: number;
  companies_skipped: number;
  schools_added: number;
  schools_updated: number;
  schools_skipped: number;
  candidates_added: number;
  candidates_updated: number;
  candidates_skipped: number;
}

export async function startImport(navigate: NavigateFunction, file: File): Promise<{ job_id: string; status: string; total: number }> {
  const res = await apiFetch(`${API_URL}/admin/data-transfer/import`, {
    method: "POST",
    headers: { "Content-Type": "application/x-ndjson" },
    body: file,
  }, navigate);
  if (!res?.ok) throw new Error("Erreur lors du lancement de l'import");
  return res.json();
}

export async function getImportJob(navigate: NavigateFunction, jobId: string): Promise<ImportJobResponse> {
  const res = await apiFetch(`${API_URL}/admin/data-transfer/import/jobs/${jobId}`, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors de la recuperation du job");
  return res.json();
}

export async function enrichStale(navigate: NavigateFunction, entity: string, before: string): Promise<EnrichStaleResponse> {
  const res = await apiFetch(`${API_URL}/admin/enrich-stale/${entity}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ before }),
  }, navigate);
  if (!res?.ok) throw new Error(`Erreur lors de l'enrichissement des ${entity}`);
  return res.json();
}

// --------------- S3 Backups ---------------

export interface S3Backup {
  name: string;
  key: string;
  last_modified: string;
  size_bytes: number;
  start_time?: string;
  finish_time?: string;
  hostname?: string;
  compressed_size?: number;
  uncompressed_size?: number;
  is_permanent?: boolean;
}

export interface S3BackupsResponse {
  configured: boolean;
  bucket?: string;
  backups: S3Backup[];
  wal_count: number;
  wal_size_bytes?: number;
  total_size_bytes: number;
}

export async function getS3Backups(navigate: NavigateFunction): Promise<S3BackupsResponse> {
  const res = await apiFetch(`${API_URL}/admin/backups/s3`, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors de la recuperation des backups S3");
  return res.json();
}

// --------------- Services Healthcheck ---------------

export interface ServiceCheck {
  status: "ok" | "error";
  latency_ms: number;
  detail?: string;
  error?: string;
}

export interface ServicesHealthResponse {
  services: Record<string, ServiceCheck>;
}

export async function getServicesHealth(navigate: NavigateFunction): Promise<ServicesHealthResponse> {
  const res = await apiFetch(`${API_URL}/admin/health/services`, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors du check des services");
  return res.json();
}

// --------------- n8n Operations ---------------

export interface N8nWorkflow {
  id: string;
  name: string;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
  tags?: string[];
}

export interface N8nExecution {
  id: string;
  workflowId: string;
  status?: string;
  mode?: string;
  startedAt?: string;
  stoppedAt?: string;
  finished?: boolean;
  retryOf?: string | null;
  waitTill?: string | null;
}

export async function listN8nWorkflows(navigate: NavigateFunction): Promise<{ workflows: N8nWorkflow[] }> {
  const res = await apiFetch(`${API_URL}/admin/n8n/workflows`, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors de la recuperation des workflows n8n");
  return res.json();
}

export async function activateN8nWorkflow(navigate: NavigateFunction, workflowId: string): Promise<void> {
  const res = await apiFetch(`${API_URL}/admin/n8n/workflows/${workflowId}/activate`, { method: "POST" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors de l'activation du workflow");
}

export async function deactivateN8nWorkflow(navigate: NavigateFunction, workflowId: string): Promise<void> {
  const res = await apiFetch(`${API_URL}/admin/n8n/workflows/${workflowId}/deactivate`, { method: "POST" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors de la desactivation du workflow");
}

export async function listN8nExecutions(
  navigate: NavigateFunction,
  params: { workflowId?: string; status?: string; limit?: number } = {},
): Promise<{ executions: N8nExecution[]; nextCursor?: string }> {
  const qs = new URLSearchParams();
  if (params.workflowId) qs.set("workflowId", params.workflowId);
  if (params.status) qs.set("status", params.status);
  qs.set("limit", String(params.limit ?? 50));
  const res = await apiFetch(`${API_URL}/admin/n8n/executions?${qs.toString()}`, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors de la recuperation des executions n8n");
  return res.json();
}

export async function stopN8nExecution(navigate: NavigateFunction, executionId: string): Promise<void> {
  const res = await apiFetch(`${API_URL}/admin/n8n/executions/${executionId}/stop`, { method: "POST" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors de l'arret de l'execution");
}

export async function deleteN8nExecution(navigate: NavigateFunction, executionId: string): Promise<void> {
  const res = await apiFetch(`${API_URL}/admin/n8n/executions/${executionId}`, { method: "DELETE" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors de la suppression de l'execution");
}

export async function stopAllN8nExecutionsForWorkflow(
  navigate: NavigateFunction,
  workflowId: string,
): Promise<{ success: string[]; failed: string[]; errors: string[] }> {
  const res = await apiFetch(`${API_URL}/admin/n8n/workflows/${workflowId}/stop-all`, { method: "POST" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors de l'arret des executions");
  return res.json();
}

// --------------- Sécurité (ClamAV diagnostic) ---------------

export interface ClamavStatus {
  enabled: boolean;
  host: string;
  port: number;
  reachable: boolean;
  latency_ms: number | null;
  version: string | null;
  error: string | null;
  error_type: string | null;
}

export interface ClamavScanResult {
  verdict: "OK" | "FOUND" | "ERROR" | "DISABLED" | "UNREACHABLE";
  signature: string | null;
  filename: string | null;
  size_bytes: number;
  declared_mime: string | null;
  real_mime: string | null;
  latency_ms: number;
  error: string | null;
}

export async function getClamavStatus(navigate: NavigateFunction): Promise<ClamavStatus> {
  const res = await apiFetch(
    `${API_URL}/admin/security/clamav/status`,
    { method: "GET" },
    navigate,
  );
  if (!res?.ok) throw new Error("Erreur lors de la recuperation du statut ClamAV");
  return res.json();
}

export async function testClamavScan(
  navigate: NavigateFunction,
  file: File,
): Promise<ClamavScanResult> {
  const formData = new FormData();
  formData.append("file", file);
  const res = await apiFetch(
    `${API_URL}/admin/security/clamav/scan-test`,
    { method: "POST", body: formData },
    navigate,
  );
  if (!res?.ok) throw new Error("Echec du scan ClamAV");
  return res.json();
}
